import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Banner,
  Button,
  InlineError,
  Layout,
  LegacyCard,
  Select,
  TextField,
} from "@shopify/polaris";
import SuccessBanner from "../../../components/banner/SuccessBanner";
import CriticalBanner from "../../../components/banner/criticalBanner";
import Transcript from "../../../components/transcript";
import {
  getpaypalCheckerData,
  resetPaypalChecker,
} from "../../../redux/store/paypalChecker/Action";
import { paypalSettingApi } from "../../../redux/troubleshooting/paypalSetting/Action";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./style.css";

const emptyValidation = {
  merchantId: "",
  transactionId: "",
  trackingId: "",
};

const PaypalChecker = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeShopId = String(id ?? "");
  const dispatch = useDispatch();
  const activeSubmission = useRef(null);

  const [accountID, setAccountID] = useState("");
  const [transactionID, setTransactionID] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [validation, setValidation] = useState(emptyValidation);
  const [locallySubmittingFor, setLocallySubmittingFor] = useState(null);

  const paypalCheckerData = useSelector(
    (store) => store.requiredPaypalCheckerReducer
  );
  const paypalSettingData = useSelector(
    (store) => store.requriedPaypalSettingData
  );

  const settingsBelongToRoute =
    paypalSettingData?.shopId === routeShopId;
  const checkerBelongsToRoute =
    paypalCheckerData?.shopId === routeShopId;
  const checkerResult =
    checkerBelongsToRoute && paypalCheckerData.isSuccess
      ? paypalCheckerData.paypalchecker
      : null;
  const checkerError =
    checkerBelongsToRoute && paypalCheckerData.isError
      ? paypalCheckerData.error
      : null;

  const merchantIdOptions = useMemo(() => {
    if (
      !settingsBelongToRoute ||
      !paypalSettingData.isSuccess ||
      !Array.isArray(paypalSettingData.data?.content)
    ) {
      return [];
    }

    const seen = new Set();
    return paypalSettingData.data.content.reduce((options, setting) => {
      const merchantAccountId = setting?.merchantAccountId;
      if (
        typeof merchantAccountId === "string" &&
        merchantAccountId.trim() &&
        !seen.has(merchantAccountId)
      ) {
        seen.add(merchantAccountId);
        options.push({
          label: merchantAccountId,
          value: merchantAccountId,
        });
      }
      return options;
    }, []);
  }, [paypalSettingData.data, paypalSettingData.isSuccess, settingsBelongToRoute]);

  useEffect(() => {
    if (routeShopId) {
      dispatch(
        paypalSettingApi({
          storeId: routeShopId,
          pageNumber: 0,
          pageSize: 100,
        })
      );
    }
  }, [dispatch, routeShopId]);

  useEffect(() => {
    activeSubmission.current = null;
    setLocallySubmittingFor(null);
    setAccountID("");
    setTransactionID("");
    setTrackingNumber("");
    setValidation(emptyValidation);
    dispatch(resetPaypalChecker());
  }, [dispatch, routeShopId]);

  useEffect(() => {
    if (
      accountID &&
      !merchantIdOptions.some((option) => option.value === accountID)
    ) {
      setAccountID("");
    }
  }, [accountID, merchantIdOptions]);

  useEffect(() => {
    const settingsAuthError =
      settingsBelongToRoute &&
      (paypalSettingData?.data?.message === "Actual error message" ||
        paypalSettingData?.error === "Actual error message");
    const checkerAuthError =
      checkerBelongsToRoute &&
      (paypalCheckerData?.paypalchecker?.message === "Actual error message" ||
        paypalCheckerData?.error === "Actual error message");
    if (settingsAuthError || checkerAuthError) navigate("/logout");
  }, [
    checkerBelongsToRoute,
    navigate,
    paypalCheckerData.error,
    paypalCheckerData.paypalchecker,
    paypalSettingData.data,
    paypalSettingData.error,
    settingsBelongToRoute,
  ]);

  const isSubmitting =
    (checkerBelongsToRoute && paypalCheckerData.isRequest) ||
    locallySubmittingFor === routeShopId;

  const handleSubmit = useCallback(async () => {
    const targetShopId = routeShopId;
    if (!targetShopId || activeSubmission.current || isSubmitting) return;

    if (!accountID) {
      setValidation({
        merchantId: "Please select a merchant account ID",
        transactionId: "",
        trackingId: "",
      });
      return;
    }
    if (!transactionID.trim()) {
      setValidation({
        merchantId: "",
        transactionId: "Please add a transaction ID",
        trackingId: "",
      });
      return;
    }
    if (!trackingNumber.trim()) {
      setValidation({
        merchantId: "",
        transactionId: "",
        trackingId: "Please add a tracking number",
      });
      return;
    }

    const submission = { shopId: targetShopId };
    activeSubmission.current = submission;
    setLocallySubmittingFor(targetShopId);
    setValidation(emptyValidation);

    try {
      await dispatch(
        getpaypalCheckerData({
          shopId: targetShopId,
          shopMerchantAccountId: accountID,
          transactionId: transactionID.trim(),
          trackingNumber: trackingNumber.trim(),
        })
      );
    } finally {
      if (activeSubmission.current === submission) {
        activeSubmission.current = null;
        setLocallySubmittingFor(null);
      }
    }
  }, [
    accountID,
    dispatch,
    isSubmitting,
    routeShopId,
    trackingNumber,
    transactionID,
  ]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const settingsError =
    settingsBelongToRoute && paypalSettingData.isError
      ? paypalSettingData.error
      : null;
  const settingsReady =
    settingsBelongToRoute && paypalSettingData.isSuccess;
  const resultMessage = checkerResult?.message || checkerError || "";
  const resultSucceeded = checkerResult?.success === true;
  const hasResult = Boolean(checkerResult || checkerError);
  const transcript = checkerResult?.responseData;

  return (
    <Layout.Section>
      <div className="wrapper">
        <LegacyCard sectioned>
          <div style={{ padding: "15px" }}>
            <LegacyCard sectioned>
              {!settingsReady ? (
                <div style={{ marginBottom: "16px" }}>
                  <Banner
                    title={
                      settingsError
                        ? "Merchant accounts could not be loaded"
                        : "Merchant accounts are loading for this store"
                    }
                    status={settingsError ? "critical" : "info"}
                  >
                    {settingsError ? <p>{settingsError}</p> : null}
                  </Banner>
                </div>
              ) : merchantIdOptions.length === 0 ? (
                <div style={{ marginBottom: "16px" }}>
                  <Banner
                    title="No PayPal merchant accounts are available for this store"
                    status="warning"
                  />
                </div>
              ) : null}

              <div
                className="d-flex flex-wrap"
                style={{
                  alignItems: "end",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <div className="textFieldContainer">
                  <Select
                    label="Merchant account ID"
                    options={merchantIdOptions}
                    onChange={setAccountID}
                    value={accountID}
                    placeholder="Select Merchant account ID"
                    disabled={!settingsReady || merchantIdOptions.length === 0 || isSubmitting}
                  />
                  <div style={{ position: "absolute" }}>
                    <InlineError
                      message={validation.merchantId}
                      fieldID="paypal-merchant-account-id"
                    />
                  </div>
                </div>

                <div onKeyDown={handleKeyPress} className="textFieldContainer">
                  <TextField
                    label="Transaction ID"
                    value={transactionID}
                    onChange={setTransactionID}
                    autoComplete="off"
                    placeholder="Please enter Transaction ID"
                    disabled={isSubmitting}
                  />
                  <div style={{ position: "absolute" }}>
                    <InlineError
                      message={validation.transactionId}
                      fieldID="paypal-transaction-id"
                    />
                  </div>
                </div>
                <div onKeyDown={handleKeyPress} className="textFieldContainer">
                  <TextField
                    label="Tracking number"
                    value={trackingNumber}
                    onChange={setTrackingNumber}
                    autoComplete="off"
                    placeholder="Please enter Tracking Number"
                    disabled={isSubmitting}
                  />
                  <div style={{ position: "absolute" }}>
                    <InlineError
                      message={validation.trackingId}
                      fieldID="paypal-tracking-number"
                    />
                  </div>
                </div>

                <Button
                  primary
                  loading={isSubmitting}
                  disabled={isSubmitting || !settingsReady || merchantIdOptions.length === 0}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </div>

              {hasResult && resultSucceeded ? (
                <SuccessBanner runTime={1} message={resultMessage} />
              ) : hasResult ? (
                <CriticalBanner runTime={1} message={resultMessage} />
              ) : null}
              {checkerResult && transcript !== null && transcript !== undefined ? (
                <Transcript
                  displayticket={checkerResult.createTicket}
                  transcript={transcript}
                />
              ) : null}
            </LegacyCard>
          </div>
        </LegacyCard>
      </div>
    </Layout.Section>
  );
};

export default PaypalChecker;
